import { forwardRef, ReactElement, Ref, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton, Slide,
  Tooltip, useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { getUID, onCapture } from "../../system/utils";
import { TransitionProps } from "@mui/material/transitions";

interface IInternalDetailProps {
  title?: string;
  children?: ReactElement | ReactElement[];
  closed: () => void;
  fullWidth?: boolean;
  fullScreen?: true;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  disabledBackground?: true;
  disableScroll?: true;
}

const Transition = forwardRef((
  { children, ...props }: TransitionProps & {
    children: ReactElement;
  },
  ref: Ref<unknown>,
) => {
  return <Slide direction="up" ref={ref} {...props} >{children}</Slide>;
});

const Detail = ({ title, children, closed, fullWidth, maxWidth, fullScreen, disabledBackground, disableScroll }: IInternalDetailProps): ReactElement =>{
  const theme = useTheme();
  const [ waiting, setWaiting ] = useState<boolean>(false);
  const [ hash ] = useState<string>(getUID());

  const processCapture = () => {
    setWaiting(true);

    setTimeout(() => {
      onCapture(hash, title ?? "dialog-content")
        .then(() => {
          setWaiting(false);
        });
    }, 500);
  };

  return(
    <Dialog onClose={closed} aria-labelledby="customized-dialog-title" open={true}
      fullWidth={fullWidth} fullScreen={fullScreen}
      maxWidth={maxWidth} slots={fullScreen ? { transition: Transition } : undefined}>
      {title &&
        <DialogTitle id="customized-dialog-title">
          <div className="display-flex-row">
            {title}
            <div className="flex-grow-1" />
            {waiting
              ? <CircularProgress className=""/>
              : <IconButton onClick={processCapture} className="p-0">
                <Tooltip title={"Snapshot"}>
                  <CameraAltIcon/>
                </Tooltip>
              </IconButton>
            }
          </div>
        </DialogTitle>
      }
      <DialogContent dividers={true} >
        <div id={hash} className={`p-2 ${disableScroll ? "scroll-hidden" : "scroll-auto"}`} style={{ position: "relative", background: disabledBackground ? undefined : theme.palette.background.paper }}>
          <div style={{ display: "inline" }}>
            {children}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={closed} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Detail;
